import React from 'react';
import { useNavigate } from 'react-router-dom';
import CodeBadge from './CodeBadge';
import { Squad } from '../../../types';
import { calculateClassesLeft } from '../utils/scheduleUtils';
import { hasIncompleteTeacherLogistics } from '../utils/teacherLogisticsCompleteness';
import { dayCodeForYmd } from '../utils/singleDaySchedule';

interface HeaderBarProps {
  title: string;
  code: string;
  squad: Squad;
  onInfoClick: () => void;
  /** Music-only chrome: home button, class code, Blizzard Beats mystery box */
  showMusicExtras?: boolean;
}

const HeaderBar: React.FC<HeaderBarProps> = ({
  title,
  code,
  squad,
  onInfoClick,
  showMusicExtras = true,
}) => {
  const navigate = useNavigate();
  const showLogisticsIncomplete = hasIncompleteTeacherLogistics(squad);

  const mainDate = squad.schedule?.mainDate;
  const classesLeft = calculateClassesLeft(
    mainDate || squad.schedule?.startDate,
    mainDate || squad.schedule?.endDate,
    mainDate ? dayCodeForYmd(mainDate) : squad.schedule?.dayOfWeek,
    [...(squad.schedule?.exceptions || []), ...(squad.schedule?.inheritedExceptions || [])]
  );

  return (
    <>
      <div className="squad-dashboard-header">
        <div className="header-left">
          {showMusicExtras && (
            <button onClick={() => navigate('/app/squads', { state: { squadsTab: 'dashboard' } })} className="back-button">
              &larr; 🏠 Home
            </button>
          )}
        </div>

        <h1 className="squad-header-title">
          {title}
        </h1>

        {/* Right-side spacer to keep title centered */}
        <div className="squad-header-spacer" />
      </div>

      <div className="squad-actions-row">
        {/* Left spacer for grid balance */}
        <div className="squad-actions-spacer" />

        <div className="squad-details-and-code">
          <button onClick={onInfoClick} className="info-button">
            Squad Details
            {showLogisticsIncomplete && (
              <span
                className="squad-details-incomplete-badge"
                aria-label="Teaching logistics incomplete"
                title="Teaching logistics incomplete"
              >
                !
              </span>
            )}
          </button>
          {showMusicExtras && <CodeBadge code={code} />}
        </div>

        {/* Right-side content (Mystery Box) — music classes only */}
        <div className="squad-actions-right">
          {showMusicExtras && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
              {/* Banner title above the box */}
              <div
                style={{
                  padding: '3px 6px',
                  borderRadius: 6,
                  fontWeight: 800,
                  fontSize: 9,
                  letterSpacing: 0.3,
                  color: '#0b1020',
                  background: 'linear-gradient(135deg, #60A5FA 0%, #A78BFA 50%, #E0E7FF 100%)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                  marginBottom: -4,
                  zIndex: 1,
                  marginRight: 8
                }}
              >
                Blizzard Beats Mystery Box
              </div>

              <div
                className="mystery-box-container"
                style={{
                  position: 'relative',
                  width: 120,
                  aspectRatio: '1',
                  borderRadius: 12,
                  overflow: 'hidden',
                  flex: '0 0 auto',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                  background: 'linear-gradient(135deg, #1e3a8a 0%, #312e81 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {/* Question mark icon */}
                <div
                  style={{
                    fontSize: 64,
                    fontWeight: 900,
                    color: '#ffffff',
                    textShadow: '0 4px 12px rgba(0,0,0,0.4)'
                  }}
                >
                  ?
                </div>

                {classesLeft > 0 && (
                  <div
                    className="classes-left-overlay"
                    style={{
                      position: 'absolute',
                      right: 4,
                      bottom: 4,
                      background: '#ef4444',
                      color: 'white',
                      padding: '3px 6px',
                      borderRadius: 999,
                      fontSize: 9,
                      fontWeight: 700,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.25)'
                    }}
                  >
                    {classesLeft} left
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="squad-header-inline">
        <div className="squad-title-block">
          <div className="squad-title-with-classes">
            {/* Title moved to header */}
          </div>
          {/* Buttons moved to centered container */}
        </div>


        {/* Leafy Lands Mystery Box video (kept outside the details/code row to avoid pushing layout) */}
        {/* Mystery Box moved to header */}
      </div >
    </>
  );
};

export default HeaderBar;
